import { Box, Grid, Typography } from '@mui/material';
import { InfoWindowF, MarkerF } from '@react-google-maps/api';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  transformAndSortDropoffData,
  transformAndSortPickupData,
  transformAndSortPickupDropoffData,
} from '@/components/pages/drive/details/driveDetailsMarkerUtils';
import * as bluGrnSvg from '@/components/shared/image/blu-grn-pin.svg';
import * as bluSvg from '@/components/shared/image/blu-pin.svg';
import * as grnSvg from '@/components/shared/image/grn-pin.svg';
import type { DropoffInformationType } from '@/features/map/types/dropoffInformationType';
import type { PickupDropoffInformationType } from '@/features/map/types/pickupDropoffInformationType';
import type { PickupInformationType } from '@/features/map/types/pickupInformationType';
import type { WaypointType } from '@/features/map/types/waypointType';

type GroupedInfoWindowRowProps = {
  item: WaypointType['reservationIdList'][number];
  groupNum: number;
  index2: number;
  markerData: WaypointType;
  labelKey: 'embark' | 'disembark';
  detailType: 1 | 2;
  t: (key: string) => string;
  convertTimeFormat: (dateTimeString: string) => string;
  getLastNameHiragana: (userName: string) => string;
  onMapDetailButtonClick: (id: string, reservationId: number, type: number) => void;
};

const GroupedInfoWindowRow = ({
  item,
  groupNum,
  index2,
  markerData,
  labelKey,
  detailType,
  t,
  convertTimeFormat,
  getLastNameHiragana,
  onMapDetailButtonClick,
}: GroupedInfoWindowRowProps) => (
  <Grid item xs={12} key={`${markerData.waypointId},${item.reservationId},${index2}`}>
    {index2 > 0 && <Box sx={{ marginTop: '50px' }} />}
    <Grid container>
      <Grid item xs={4}>
        <Typography align="left" sx={{ fontSize: '20px' }}>
          {groupNum}
          {t('driveDetail.group')}
        </Typography>
      </Grid>
      <Grid item xs={8} />
      <Grid item xs={4}>
        <Typography align="left" sx={{ fontSize: '20px' }}>
          {convertTimeFormat(item.latestEstimatedDatetime)}
        </Typography>
      </Grid>
      <Grid item xs={8} />
      <Grid item xs={1}>
        <Typography align="left" noWrap sx={{ fontWeight: '600', fontSize: '20px' }}>
          {t(`driveDetail.${labelKey}`)}
        </Typography>
      </Grid>
      <Grid item xs={11}>
        <Typography noWrap align="left" sx={{ fontSize: '20px' }}>
          {markerData.waypointNumber}
          {markerData.waypointNumber ? '.' : ''}
          {markerData.waypointName}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'left' }}>
          <Typography
            noWrap
            className="text-underline"
            onClick={() => onMapDetailButtonClick(markerData.waypointId.toString(), item.reservationId, detailType)}
          >
            {item.userName ? getLastNameHiragana(item.userName) : ''}
          </Typography>
        </Box>
      </Grid>
    </Grid>
  </Grid>
);

type PickupDropoffGroupedInfoWindowRowProps = {
  item: WaypointType['reservationIdList'][number] & { pickupDropoffKbn: number };
  groupNum: number;
  index2: number;
  markerData: WaypointType;
  t: (key: string) => string;
  convertTimeFormat: (dateTimeString: string) => string;
  getLastNameHiragana: (userName: string) => string;
  onMapDetailButtonClick: (id: string, reservationId: number, type: number) => void;
};

const PickupDropoffGroupedInfoWindowRow = ({
  item,
  groupNum,
  index2,
  markerData,
  t,
  convertTimeFormat,
  getLastNameHiragana,
  onMapDetailButtonClick,
}: PickupDropoffGroupedInfoWindowRowProps) => (
  <Grid item xs={12} key={`${markerData.waypointId},${item.reservationId},${index2}`}>
    {index2 > 0 && <Box sx={{ marginTop: '50px' }} />}
    <Grid container>
      <Grid item xs={4}>
        <Typography align="left" sx={{ fontSize: '20px' }}>
          {groupNum}
          {t('driveDetail.group')}
        </Typography>
      </Grid>
      <Grid item xs={8} />
      <Grid item xs={4}>
        <Typography align="left" sx={{ fontSize: '20px' }}>
          {convertTimeFormat(item.latestEstimatedDatetime)}
        </Typography>
      </Grid>
      <Grid item xs={8} />
      <Grid item xs={1}>
        <Typography align="left" noWrap sx={{ fontWeight: '600', fontSize: '20px' }}>
          {item.pickupDropoffKbn === 1 ? t('driveDetail.embark') : t('driveDetail.disembark')}
        </Typography>
      </Grid>
      <Grid item xs={11}>
        <Typography noWrap align="left" sx={{ fontSize: '20px' }}>
          {markerData.waypointNumber}
          {markerData.waypointNumber ? '.' : ''}
          {markerData.waypointName}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'left' }}>
          <Typography
            noWrap
            className="text-underline"
            onClick={() =>
              onMapDetailButtonClick(markerData.waypointId.toString(), item.reservationId, item.pickupDropoffKbn)
            }
          >
            {item.userName ? getLastNameHiragana(item.userName) : ''}
          </Typography>
        </Box>
      </Grid>
    </Grid>
  </Grid>
);

type DriveDetailsMapMarkersProps = {
  pickupInformation: PickupInformationType[];
  dropoffInformation: DropoffInformationType[];
  pickupDropoffInformation: PickupDropoffInformationType[];
  pickupAreaFlg: 1 | 2;
  dropoffAreaFlg: 1 | 2;
  pickupDropoffAreaFlg: 1 | 2;
  selectedMarker: string | undefined;
  location: google.maps.LatLngLiteral | null;
  onMarkerClick: (markerId: string) => void;
  onMapDetailButtonClick: (id: string, reservationId: number, type: number) => void;
  convertTimeFormat: (dateTimeString: string) => string;
  getLastNameHiragana: (userName: string) => string;
};

const renderGroups = (count: number, t: (key: string) => string) => (
  <span>
    {Array.from(Array(count).keys())
      .map((i) => i + 1)
      .join(',')}
    {t('driveDetail.group')}
  </span>
);

type MarkerBuilderParams = DriveDetailsMapMarkersProps & {
  t: (key: string) => string;
  googleApi: typeof google;
  setSelectedMarkerEmpty: () => void;
};

const buildPickupMarkers = ({
  pickupAreaFlg,
  pickupInformation,
  selectedMarker,
  onMarkerClick,
  onMapDetailButtonClick,
  convertTimeFormat,
  getLastNameHiragana,
  t,
  googleApi,
  setSelectedMarkerEmpty,
}: MarkerBuilderParams): JSX.Element[] | null => {
  let pickupCounter = 0;

  if (pickupAreaFlg === 1) {
    return pickupInformation.map((markerData) => (
      <MarkerF
        key={markerData.pickupWaypointId}
        position={markerData.pickupWaypointPosition}
        icon={{ url: bluSvg.default || '', scaledSize: new googleApi.maps.Size(50, 50) }}
        onClick={() => onMarkerClick(markerData.pickupWaypointId.toString())}
      >
        {selectedMarker === markerData.pickupWaypointId.toString() && (
          <InfoWindowF position={markerData.pickupWaypointPosition} onCloseClick={setSelectedMarkerEmpty}>
            <Box sx={{ width: '300px', pt: 1 }}>
              <Grid container sx={{ wordBreak: 'break-all' }}>
                <Grid item xs={4}>
                  <Typography align="left" noWrap sx={{ fontSize: '20px' }}>
                    {renderGroups(markerData.reservationIdList.length, t)}{' '}
                  </Typography>
                </Grid>
                <Grid item xs={8} />
                <Grid item xs={4}>
                  <Typography align="left" sx={{ fontSize: '20px' }}>
                    {convertTimeFormat(markerData.latestEstimatedPickupDatetime)}
                  </Typography>
                </Grid>
                <Grid item xs={8} />
                <Grid item xs={1}>
                  <Typography align="left" noWrap sx={{ fontWeight: '600', fontSize: '20px' }}>
                    {t('driveDetail.embark')}
                  </Typography>
                </Grid>
                <Grid item xs={11}>
                  <Typography noWrap align="left" sx={{ fontSize: '20px' }}>
                    {markerData.pickupWaypointNumber}
                    {markerData.pickupWaypointNumber ? '.' : ''}
                    {markerData.pickupWaypointName}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  {markerData.reservationIdList.map((item) => (
                    <Box
                      sx={{ display: 'flex', justifyContent: 'left' }}
                      key={`${markerData.pickupWaypointId},${item.reservationId}`}
                    >
                      <Typography
                        noWrap
                        className="text-underline"
                        onClick={() =>
                          onMapDetailButtonClick(markerData.pickupWaypointId.toString(), item.reservationId, 1)
                        }
                      >
                        {item.userName ? getLastNameHiragana(item.userName) : ''}
                      </Typography>
                    </Box>
                  ))}
                </Grid>
              </Grid>
            </Box>
          </InfoWindowF>
        )}
      </MarkerF>
    ));
  }

  if (pickupAreaFlg === 2) {
    return transformAndSortPickupData(pickupInformation).map((markerData) => {
      const itemsWithGroup = markerData.reservationIdList.map((item) => {
        pickupCounter += 1;

        return { item, groupNum: pickupCounter };
      });

      return (
        <MarkerF
          key={markerData.waypointId}
          position={markerData.waypointPosition}
          icon={{ url: bluSvg.default || '', scaledSize: new googleApi.maps.Size(50, 50) }}
          onClick={() => onMarkerClick(markerData.waypointId.toString())}
        >
          {selectedMarker === markerData.waypointId.toString() && (
            <InfoWindowF position={markerData.waypointPosition} onCloseClick={setSelectedMarkerEmpty}>
              <Box sx={{ width: '300px', pt: 1 }}>
                <Grid container sx={{ wordBreak: 'break-all' }}>
                  {itemsWithGroup.map(({ item, groupNum }, index2) => (
                    <GroupedInfoWindowRow
                      key={`${markerData.waypointId},${item.reservationId}`}
                      item={item}
                      groupNum={groupNum}
                      index2={index2}
                      markerData={markerData}
                      labelKey="embark"
                      detailType={1}
                      t={t}
                      convertTimeFormat={convertTimeFormat}
                      getLastNameHiragana={getLastNameHiragana}
                      onMapDetailButtonClick={onMapDetailButtonClick}
                    />
                  ))}
                </Grid>
              </Box>
            </InfoWindowF>
          )}
        </MarkerF>
      );
    });
  }

  return null;
};

const buildDropoffMarkers = ({
  dropoffAreaFlg,
  dropoffInformation,
  selectedMarker,
  onMarkerClick,
  onMapDetailButtonClick,
  convertTimeFormat,
  getLastNameHiragana,
  t,
  googleApi,
  setSelectedMarkerEmpty,
}: MarkerBuilderParams): JSX.Element[] | null => {
  let dropoffCounter = 0;

  if (dropoffAreaFlg === 1) {
    return dropoffInformation.map((markerData) => (
      <MarkerF
        key={markerData.dropoffWaypointId}
        position={markerData.dropoffWaypointPosition}
        icon={{ url: grnSvg.default || '', scaledSize: new googleApi.maps.Size(50, 50) }}
        onClick={() => onMarkerClick(markerData.dropoffWaypointId.toString())}
      >
        {selectedMarker === markerData.dropoffWaypointId.toString() && (
          <InfoWindowF position={markerData.dropoffWaypointPosition} onCloseClick={setSelectedMarkerEmpty}>
            <Box sx={{ width: '300px', pt: 1 }}>
              <Grid container sx={{ wordBreak: 'break-all' }}>
                <Grid item xs={4}>
                  <Typography align="left" noWrap sx={{ fontSize: '20px' }}>
                    {renderGroups(markerData.reservationIdList.length, t)}{' '}
                  </Typography>
                </Grid>
                <Grid item xs={8} />
                <Grid item xs={4}>
                  <Typography align="left" sx={{ fontSize: '20px' }}>
                    {convertTimeFormat(markerData.latestEstimatedDropoffDatetime)}
                  </Typography>
                </Grid>
                <Grid item xs={8} />
                <Grid item xs={1}>
                  <Typography align="left" noWrap sx={{ fontWeight: '600', fontSize: '20px' }}>
                    {t('driveDetail.disembark')}
                  </Typography>
                </Grid>
                <Grid item xs={11}>
                  <Typography noWrap align="left" sx={{ fontSize: '20px' }}>
                    {markerData.dropoffWaypointNumber}
                    {markerData.dropoffWaypointNumber ? '.' : ''}
                    {markerData.dropoffWaypointName}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  {markerData.reservationIdList.map((item) => (
                    <Box
                      sx={{ display: 'flex', justifyContent: 'left' }}
                      key={`${markerData.dropoffWaypointId},${item.reservationId}`}
                    >
                      <Typography
                        noWrap
                        className="text-underline"
                        onClick={() =>
                          onMapDetailButtonClick(markerData.dropoffWaypointId.toString(), item.reservationId, 2)
                        }
                      >
                        {item.userName ? getLastNameHiragana(item.userName) : ''}
                      </Typography>
                    </Box>
                  ))}
                </Grid>
              </Grid>
            </Box>
          </InfoWindowF>
        )}
      </MarkerF>
    ));
  }

  if (dropoffAreaFlg === 2) {
    return transformAndSortDropoffData(dropoffInformation).map((markerData) => {
      const itemsWithGroup = markerData.reservationIdList.map((item) => {
        dropoffCounter += 1;

        return { item, groupNum: dropoffCounter };
      });

      return (
        <MarkerF
          key={markerData.waypointId}
          position={markerData.waypointPosition}
          icon={{ url: grnSvg.default || '', scaledSize: new googleApi.maps.Size(50, 50) }}
          onClick={() => onMarkerClick(markerData.waypointId.toString())}
        >
          {selectedMarker === markerData.waypointId.toString() && (
            <InfoWindowF position={markerData.waypointPosition} onCloseClick={setSelectedMarkerEmpty}>
              <Box sx={{ width: '300px', pt: 1 }}>
                <Grid container sx={{ wordBreak: 'break-all' }}>
                  {itemsWithGroup.map(({ item, groupNum }, index2) => (
                    <GroupedInfoWindowRow
                      key={`${markerData.waypointId},${item.reservationId}`}
                      item={item}
                      groupNum={groupNum}
                      index2={index2}
                      markerData={markerData}
                      labelKey="disembark"
                      detailType={2}
                      t={t}
                      convertTimeFormat={convertTimeFormat}
                      getLastNameHiragana={getLastNameHiragana}
                      onMapDetailButtonClick={onMapDetailButtonClick}
                    />
                  ))}
                </Grid>
              </Box>
            </InfoWindowF>
          )}
        </MarkerF>
      );
    });
  }

  return null;
};

const buildPickupDropoffMarkers = ({
  pickupDropoffAreaFlg,
  pickupDropoffInformation,
  selectedMarker,
  onMarkerClick,
  onMapDetailButtonClick,
  convertTimeFormat,
  getLastNameHiragana,
  t,
  googleApi,
  setSelectedMarkerEmpty,
}: MarkerBuilderParams): JSX.Element[] | null => {
  if (pickupDropoffAreaFlg === 1) {
    return pickupDropoffInformation.map((markerData) => (
      <MarkerF
        key={`${markerData.pickupWaypointId},${markerData.dropoffWaypointId}`}
        position={markerData.pickupWaypointPosition}
        icon={{ url: bluGrnSvg.default || '', scaledSize: new googleApi.maps.Size(50, 50) }}
        onClick={() => onMarkerClick(`${markerData.pickupWaypointId},${markerData.dropoffWaypointId}`)}
      >
        {selectedMarker === `${markerData.pickupWaypointId},${markerData.dropoffWaypointId}` && (
          <InfoWindowF position={markerData.pickupWaypointPosition} onCloseClick={setSelectedMarkerEmpty}>
            <Box sx={{ width: '300px', pt: 1 }}>
              <Grid container sx={{ wordBreak: 'break-all' }}>
                <Grid item xs={4}>
                  <Typography align="left" sx={{ fontSize: '20px' }}>
                    {renderGroups(
                      markerData.pickupReservationIdList.length + markerData.dropoffReservationIdList.length,
                      t,
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={8} />
                <Grid item xs={4}>
                  <Typography align="left" sx={{ fontSize: '20px' }}>
                    {convertTimeFormat(markerData.latestEstimatedPickupDatetime)}
                  </Typography>
                </Grid>
                <Grid item xs={8} />
                <Grid item xs={1}>
                  <Typography align="left" noWrap sx={{ fontWeight: '600', fontSize: '20px' }}>
                    {t('driveDetail.disembark')}
                  </Typography>
                </Grid>
                <Grid item xs={11}>
                  <Typography noWrap align="left" sx={{ fontSize: '20px' }}>
                    {markerData.dropoffWaypointNumber}
                    {markerData.dropoffWaypointNumber ? '.' : ''}
                    {markerData.dropoffWaypointName}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  {markerData.dropoffReservationIdList.map((item) => (
                    <Box
                      sx={{ display: 'flex', justifyContent: 'left' }}
                      key={`${markerData.dropoffWaypointId},${item.reservationId}`}
                    >
                      <Typography
                        noWrap
                        className="text-underline"
                        onClick={() =>
                          onMapDetailButtonClick(markerData.dropoffWaypointId.toString(), item.reservationId, 2)
                        }
                      >
                        {item.userName ? getLastNameHiragana(item.userName) : ''}
                      </Typography>
                    </Box>
                  ))}
                </Grid>
                <Grid item xs={1}>
                  <Typography align="left" noWrap sx={{ fontWeight: '600', fontSize: '20px' }}>
                    {t('driveDetail.embark')}
                  </Typography>
                </Grid>
                <Grid item xs={11}>
                  <Typography noWrap align="left" sx={{ fontSize: '20px' }}>
                    {markerData.pickupWaypointNumber}
                    {markerData.pickupWaypointNumber ? '.' : ''}
                    {markerData.pickupWaypointName}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  {markerData.pickupReservationIdList.map((item) => (
                    <Box
                      sx={{ display: 'flex', justifyContent: 'left' }}
                      key={`${markerData.pickupWaypointId},${item.reservationId}`}
                    >
                      <Typography
                        noWrap
                        className="text-underline"
                        onClick={() =>
                          onMapDetailButtonClick(markerData.pickupWaypointId.toString(), item.reservationId, 1)
                        }
                      >
                        {item.userName ? getLastNameHiragana(item.userName) : ''}
                      </Typography>
                    </Box>
                  ))}
                </Grid>
              </Grid>
            </Box>
          </InfoWindowF>
        )}
      </MarkerF>
    ));
  }

  if (pickupDropoffAreaFlg === 2) {
    return transformAndSortPickupDropoffData(pickupDropoffInformation).map((markerData) => {
      let pickupDropoffCounter = 0;

      return (
        <MarkerF
          key={markerData.waypointId}
          position={markerData.waypointPosition}
          icon={{ url: bluGrnSvg.default || '', scaledSize: new googleApi.maps.Size(50, 50) }}
          onClick={() => onMarkerClick(`${markerData.waypointId},${markerData.waypointId}`)}
        >
          {selectedMarker === `${markerData.waypointId},${markerData.waypointId}` && (
            <InfoWindowF position={markerData.waypointPosition} onCloseClick={setSelectedMarkerEmpty}>
              <Box sx={{ width: '300px', pt: 1 }}>
                <Grid container sx={{ wordBreak: 'break-all' }}>
                  {markerData.reservationIdList.map((item, index2) => {
                    pickupDropoffCounter += 1;

                    return (
                      <PickupDropoffGroupedInfoWindowRow
                        key={`${markerData.waypointId},${item.reservationId}`}
                        item={item}
                        groupNum={pickupDropoffCounter}
                        index2={index2}
                        markerData={markerData}
                        t={t}
                        convertTimeFormat={convertTimeFormat}
                        getLastNameHiragana={getLastNameHiragana}
                        onMapDetailButtonClick={onMapDetailButtonClick}
                      />
                    );
                  })}
                </Grid>
              </Box>
            </InfoWindowF>
          )}
        </MarkerF>
      );
    });
  }

  return null;
};

export const useDriveDetailsMapMarkers = (props: DriveDetailsMapMarkersProps): (string | JSX.Element | null)[] => {
  const {
    pickupInformation,
    dropoffInformation,
    pickupDropoffInformation,
    pickupAreaFlg,
    dropoffAreaFlg,
    pickupDropoffAreaFlg,
    selectedMarker,
    location,
    onMarkerClick,
    onMapDetailButtonClick,
    convertTimeFormat,
    getLastNameHiragana,
  } = props;

  const { t } = useTranslation();
  const setSelectedMarkerEmpty = useCallback(() => onMarkerClick(''), [onMarkerClick]);

  if (typeof google === 'undefined' || !google.maps || !google.maps.SymbolPath) {
    return [];
  }

  const markerBuilderParams: MarkerBuilderParams = {
    pickupInformation,
    dropoffInformation,
    pickupDropoffInformation,
    pickupAreaFlg,
    dropoffAreaFlg,
    pickupDropoffAreaFlg,
    selectedMarker,
    location,
    onMarkerClick,
    onMapDetailButtonClick,
    convertTimeFormat,
    getLastNameHiragana,
    t,
    googleApi: google,
    setSelectedMarkerEmpty,
  };

  const pickupMarkers = buildPickupMarkers(markerBuilderParams);
  const dropoffMarkers = buildDropoffMarkers(markerBuilderParams);
  const pickupDropoffMarkers = buildPickupDropoffMarkers(markerBuilderParams);

  const driverLocationMarker = location ? (
    <MarkerF
      key="drivercurrentLocation"
      position={location}
      icon={{
        path: google.maps.SymbolPath.CIRCLE,
        scale: 12,
        fillColor: '#4285F4',
        fillOpacity: 0.9,
        strokeWeight: 2,
        strokeColor: 'white',
      }}
      zIndex={(google.maps?.Marker?.MAX_ZINDEX ?? 1000000) + 1}
    />
  ) : null;

  return [
    ...(pickupMarkers ?? []),
    ...(dropoffMarkers ?? []),
    ...(pickupDropoffMarkers ?? []),
    driverLocationMarker,
  ].filter(Boolean) as (string | JSX.Element | null)[];
};

export const DriveDetailsMapMarkers: React.FC<DriveDetailsMapMarkersProps> = (props) =>
  useDriveDetailsMapMarkers(props);
